export const API_URL = process.env.REACT_APP_API_URL || (window.location.port === '80' || window.location.port === '' ? '' : `http://${window.location.hostname}:8000`);

// Offline detection
export const isOnline = () => navigator.onLine;

// Cache key generator
const getCacheKey = (endpoint) => `api_cache_${endpoint}`;

// Cache data in localStorage with LRU cleanup on quota exceeded
const CACHE_MAX_AGE = 24 * 60 * 60 * 1000; // 24 hours

const cacheResponse = (endpoint, data) => {
  try {
    const cacheKey = getCacheKey(endpoint);
    localStorage.setItem(cacheKey, JSON.stringify({ data, timestamp: Date.now() }));
  } catch (e) {
    if (e.name === 'QuotaExceededError') {
      // Collect all cache entries with their timestamps
      const cacheKeys = [];
      const now = Date.now();
      for (let i = 0; i < localStorage.length; i++) {
        const key = localStorage.key(i);
        if (key && key.startsWith('api_cache_')) {
          try {
            const item = JSON.parse(localStorage.getItem(key));
            cacheKeys.push({ key, timestamp: item?.timestamp || 0 });
          } catch { cacheKeys.push({ key, timestamp: 0 }); }
        }
      }
      // First pass: remove expired entries
      const expired = cacheKeys.filter(item => now - item.timestamp >= CACHE_MAX_AGE);
      expired.forEach(item => localStorage.removeItem(item.key));

      // If no expired entries were cleaned, remove oldest half (LRU)
      if (expired.length === 0 && cacheKeys.length > 0) {
        cacheKeys.sort((a, b) => a.timestamp - b.timestamp);
        const removeCount = Math.max(Math.ceil(cacheKeys.length / 2), 1);
        for (let i = 0; i < removeCount; i++) {
          localStorage.removeItem(cacheKeys[i].key);
        }
      }
      // Retry the save
      try {
        const cacheKey = getCacheKey(endpoint);
        localStorage.setItem(cacheKey, JSON.stringify({ data, timestamp: Date.now() }));
      } catch { /* give up silently */ }
    } else {
      console.warn('Failed to cache response:', e);
    }
  }
};

// Get cached data (valid for 24 hours for GET requests)
const getCachedResponse = (endpoint, maxAge = CACHE_MAX_AGE) => {
  try {
    const cacheKey = getCacheKey(endpoint);
    const cached = localStorage.getItem(cacheKey);
    if (cached) {
      const { data, timestamp } = JSON.parse(cached);
      if (Date.now() - timestamp < maxAge) {
        return data;
      }
    }
  } catch (e) {
    console.warn('Failed to get cached response:', e);
  }
  return null;
};

// Helper function for API calls with offline support
async function apiCall(endpoint, options = {}) {
  const token = localStorage.getItem('token');
  const isGetRequest = !options.method || options.method === 'GET';

  const defaultHeaders = {
    'Content-Type': 'application/json',
  };

  if (token) {
    defaultHeaders['Authorization'] = `Bearer ${token}`;
  }

  // Always try network first (navigator.onLine is unreliable)
  try {
    const response = await fetch(`${API_URL}${endpoint}`, {
      ...options,
      headers: {
        ...defaultHeaders,
        ...options.headers,
      },
    });

    // Try to parse response as JSON
    let data;
    const contentType = response.headers.get('content-type');
    if (contentType && contentType.includes('application/json')) {
      data = await response.json();
    } else {
      const text = await response.text();
      data = { detail: text || `HTTP error ${response.status}` };
    }

    if (!response.ok) {
      if (response.status === 404) {
        console.warn('API 404:', response.url);
      } else if (response.status === 401) {
        console.warn('API 401 (auth):', response.url);
        // Skip session-expiry handling for login endpoints (they return 401 for wrong credentials)
        const isLoginEndpoint = endpoint.includes('/login');
        if (!isLoginEndpoint) {
          // Token expired or invalid — clear stale auth and redirect to landing
          localStorage.removeItem('token');
          localStorage.removeItem('user');
          localStorage.removeItem('school');
          if (window.location.pathname !== '/') {
            window.location.href = '/';
            throw new Error('Session expired. Please log in again.');
          }
        }
      } else if (response.status >= 500) {
        console.error('API Error:', response.status, data);
      } else {
        console.warn('API Error:', response.status, data);
      }

      // Handle validation errors (Pydantic returns array of errors)
      let errorMessage;
      if (Array.isArray(data.detail)) {
        // Format validation errors nicely
        errorMessage = data.detail.map(err => {
          const field = err.loc ? err.loc[err.loc.length - 1] : 'field';
          return `${field}: ${err.msg}`;
        }).join('; ');
      } else {
        errorMessage = data.detail || `Request failed with status ${response.status}`;
      }

      throw new Error(errorMessage);
    }

    // Cache successful GET responses
    if (isGetRequest) {
      cacheResponse(endpoint, data);
    }

    return data;
  } catch (error) {
    // Network error - try cache for GET requests
    if (error.name === 'TypeError' && error.message.includes('fetch')) {
      if (isGetRequest) {
        const cached = getCachedResponse(endpoint);
        if (cached) {
          console.log('[Network Error] Returning cached data for:', endpoint);
          return cached;
        }
      }
      throw new Error('网络错误，请检查网络连接后重试 / Network error, please check your connection and try again.');
    }
    throw error;
  }
}

// Auth API
export const authAPI = {
  signup: async (userData) => {
    const response = await apiCall('/api/auth/signup', {
      method: 'POST',
      body: JSON.stringify(userData),
    });
    // Store token
    localStorage.setItem('token', response.access_token);
    localStorage.setItem('user', JSON.stringify(response.user));
    return response;
  },

  login: async (credentials) => {
    const response = await apiCall('/api/auth/login', {
      method: 'POST',
      body: JSON.stringify(credentials),
    });
    // Clear ALL previous session data before setting new
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    localStorage.removeItem('school');
    // Store new token
    localStorage.setItem('token', response.access_token);
    // Auto-detect user vs school response
    if (response.school) {
      localStorage.setItem('school', JSON.stringify(response.school));
      response._isSchool = true;
    } else {
      localStorage.setItem('user', JSON.stringify(response.user));
    }
    return response;
  },

  logout: () => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    localStorage.removeItem('school');
    // Clear any cached data that might interfere with new session
    Object.keys(localStorage).forEach(key => {
      if (key.startsWith('api_cache_') || key.startsWith('mesamind_')) {
        localStorage.removeItem(key);
      }
    });
  },

  getMe: async () => {
    return apiCall('/api/auth/me');
  },

  getStoredUser: () => {
    const user = localStorage.getItem('user');
    return user ? JSON.parse(user) : null;
  },

  isAuthenticated: () => {
    return !!localStorage.getItem('token');
  },

  // Get schools list for student registration
  getSchools: async () => {
    return apiCall('/api/auth/schools');
  },
};

// Student API
export const studentAPI = {
  // Dashboard
  getDashboard: async () => {
    return apiCall('/api/student/dashboard');
  },

  updateStreak: async () => {
    return apiCall('/api/student/update-streak', { method: 'POST' });
  },

  // Classes
  getClasses: async () => {
    return apiCall('/api/student/classes');
  },

  getAvailableClasses: async () => {
    return apiCall('/api/student/school/classes');
  },

  joinClass: async (classCode) => {
    return apiCall('/api/student/join-class', {
      method: 'POST',
      body: JSON.stringify({ class_code: classCode }),
    });
  },

  leaveClass: async (classId) => {
    return apiCall(`/api/student/classes/${classId}/leave`, {
      method: 'DELETE',
    });
  },

  // Artworks
  getArtworks: async () => {
    return apiCall('/api/student/artworks');
  },

  getArtwork: async (id) => {
    return apiCall(`/api/student/artworks/${id}`);
  },

  createArtwork: async (data) => {
    return apiCall('/api/student/artworks', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  },

  updateArtwork: async (id, data) => {
    return apiCall(`/api/student/artworks/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  },

  renameArtwork: async (id, name) => {
    return apiCall(`/api/student/artworks/${id}/rename?name=${encodeURIComponent(name)}`, {
      method: 'PATCH',
    });
  },

  deleteArtwork: async (id) => {
    return apiCall(`/api/student/artworks/${id}`, {
      method: 'DELETE',
    });
  },

  // Assignments
  getAssignments: async () => {
    return apiCall('/api/student/assignments');
  },

  getAssignment: async (id) => {
    return apiCall(`/api/student/assignments/${id}`);
  },

  // Submissions
  getSubmissions: async () => {
    return apiCall('/api/student/submissions');
  },

  createSubmission: async (data) => {
    return apiCall('/api/student/submissions', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  },

  submitArtworkToAssignment: async (artworkId, assignmentId) => {
    return apiCall('/api/student/submissions', {
      method: 'POST',
      body: JSON.stringify({
        artwork_id: artworkId,
        assignment_id: assignmentId,
      }),
    });
  },

  deleteSubmission: async (submissionId) => {
    return apiCall(`/api/student/submissions/${submissionId}`, {
      method: 'DELETE',
    });
  },

  // Behavioral Events
  recordEvent: async (eventData) => {
    return apiCall('/api/student/behavioral-events', {
      method: 'POST',
      body: JSON.stringify(eventData),
    });
  },

  recordEventsBatch: async (events) => {
    return apiCall('/api/student/behavioral-events/batch', {
      method: 'POST',
      body: JSON.stringify(events),
    });
  },
};

// Teacher API
export const teacherAPI = {
  // Dashboard
  getDashboard: async (enhanced = false) => {
    const params = enhanced ? '?enhanced=true' : '';
    return apiCall(`/api/teacher/dashboard${params}`);
  },

  // Classes
  getClasses: async () => {
    return apiCall('/api/teacher/classes');
  },

  getClassStudents: async (classId) => {
    return apiCall(`/api/teacher/classes/${classId}/students`);
  },

  // Get allowed assessment modes for teacher's school
  getAllowedAssessmentModes: async () => {
    return apiCall('/api/teacher/allowed-assessment-modes');
  },

  // Assignments
  getAssignments: async (classId = null, statusFilter = null) => {
    const params = new URLSearchParams();
    if (classId) params.append('class_id', classId);
    if (statusFilter) params.append('status_filter', statusFilter);
    const queryString = params.toString();
    return apiCall(`/api/teacher/assignments${queryString ? `?${queryString}` : ''}`);
  },

  getAssignment: async (id) => {
    return apiCall(`/api/teacher/assignments/${id}`);
  },

  createAssignment: async (data) => {
    return apiCall('/api/teacher/assignments', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  },

  updateAssignment: async (id, data) => {
    return apiCall(`/api/teacher/assignments/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  },

  deleteAssignment: async (id) => {
    return apiCall(`/api/teacher/assignments/${id}`, {
      method: 'DELETE',
    });
  },

  // Publish assignment (change from draft to published)
  publishAssignment: async (assignmentId) => {
    return apiCall(`/api/teacher/assignments/${assignmentId}/publish`, {
      method: 'POST',
    });
  },

  // Submissions
  getAssignmentSubmissions: async (assignmentId) => {
    return apiCall(`/api/teacher/assignments/${assignmentId}/submissions`);
  },

  getSubmissionDetail: async (submissionId) => {
    return apiCall(`/api/teacher/submissions/${submissionId}`);
  },

  gradeSubmission: async (submissionId, gradeData) => {
    return apiCall(`/api/teacher/submissions/${submissionId}/grade`, {
      method: 'PUT',
      body: JSON.stringify(gradeData),
    });
  },

  // Behavioral Events for Replay
  getArtworkEvents: async (artworkId) => {
    return apiCall(`/api/teacher/artworks/${artworkId}/events`);
  },

  getArtworkDetails: async (artworkId) => {
    return apiCall(`/api/teacher/artworks/${artworkId}`);
  },

  // Student Portfolio
  getStudentPortfolio: async (studentId, months = 6) => {
    return apiCall(`/api/teacher/students/${studentId}/portfolio?months=${months}`);
  },

  // Layer 2 Objective Indicators (35 indicators)
  getSubmissionObjectiveData: async (submissionId) => {
    return apiCall(`/api/teacher/submissions/${submissionId}/objective-data`);
  },

  // R1: Growth Report
  getStudentGrowthReport: async (studentId, startDate = null, endDate = null) => {
    let url = `/api/teacher/students/${studentId}/growth-report`;
    const params = [];
    if (startDate) params.push(`start_date=${startDate}`);
    if (endDate) params.push(`end_date=${endDate}`);
    if (params.length) url += '?' + params.join('&');
    return apiCall(url);
  },

  // Smart Grading Assistant
  startSmartGrading: async (assignmentId) => {
    return apiCall(`/api/teacher/assignments/${assignmentId}/smart-grading/start`, {
      method: 'POST',
    });
  },

  getNextArtwork: async (sessionId) => {
    return apiCall(`/api/teacher/smart-grading/${sessionId}/next`);
  },

  submitGradingDecision: async (sessionId, data) => {
    return apiCall(`/api/teacher/smart-grading/${sessionId}/grade`, {
      method: 'POST',
      body: JSON.stringify(data),
    });
  },

  batchAdoptSimilar: async (sessionId, artworkIds) => {
    return apiCall(`/api/teacher/smart-grading/${sessionId}/batch-adopt`, {
      method: 'POST',
      body: JSON.stringify({ artwork_ids: artworkIds }),
    });
  },

  completeSmartGrading: async (sessionId) => {
    return apiCall(`/api/teacher/smart-grading/${sessionId}/complete`, {
      method: 'POST',
    });
  },

  // Trigger AI Grading for all pending submissions
  triggerAIGrading: async (assignmentId) => {
    return apiCall(`/api/teacher/assignments/${assignmentId}/ai-grade`, {
      method: 'POST',
    });
  },

  // Save teacher's grade/correction for a submission
  saveSubmissionGrade: async (submissionId, gradeData) => {
    return apiCall(`/api/teacher/submissions/${submissionId}/grade`, {
      method: 'PUT',
      body: JSON.stringify(gradeData),
    });
  },

  // Get submission with behavioral analysis for playback
  getSubmissionPlayback: async (submissionId) => {
    return apiCall(`/api/teacher/submissions/${submissionId}/playback`);
  },

  // Alert Management
  markAlertsRead: async (alertIds) => {
    return apiCall('/api/teacher/alerts/mark-read', {
      method: 'POST',
      body: JSON.stringify({ alert_ids: alertIds }),
    });
  },

  markAlertsUnread: async (alertIds) => {
    return apiCall('/api/teacher/alerts/mark-unread', {
      method: 'POST',
      body: JSON.stringify({ alert_ids: alertIds }),
    });
  },

  markAllAlertsRead: async () => {
    return apiCall('/api/teacher/alerts/mark-all-read', {
      method: 'POST',
    });
  },

  getAllAlerts: async () => {
    return apiCall('/api/teacher/alerts/all');
  },

  // Bulk CSV: single student × all assignments
  getStudentAllObjectiveCSV: async (studentId) => {
    const response = await fetch(`${API_URL}/api/teacher/students/${studentId}/objective-data/csv`, {
      headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
    });
    if (!response.ok) throw new Error('Download failed');
    const blob = await response.blob();
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `student_all_objectives_${studentId}.csv`;
    a.click();
    window.URL.revokeObjectURL(url);
  },

  // Bulk CSV: entire class × all assignments
  getClassAllObjectiveCSV: async (classId) => {
    const response = await fetch(`${API_URL}/api/teacher/classes/${classId}/objective-data/csv`, {
      headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
    });
    if (!response.ok) throw new Error('Download failed');
    const blob = await response.blob();
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `class_all_objectives_${classId}.csv`;
    a.click();
    window.URL.revokeObjectURL(url);
  },
};

// MIND-Sentinel API (Psychological Detection)
export const mindAPI = {
  // Analyze behavioral data with optional VLM for Reverse Formation
  analyze: async (behavioralData, artworkImage = null, submissionId = null, artworkId = null) => {
    return apiCall('/api/mind/analyze', {
      method: 'POST',
      body: JSON.stringify({
        submission_id: submissionId,
        artwork_id: artworkId,
        behavioral_data: behavioralData,
        artwork_image: artworkImage, // Base64 image for VLM Reverse Formation
      }),
    });
  },

  // Analyze artwork emotion using VLM (standalone)
  analyzeEmotion: async (artworkImage, imageFormat = 'base64') => {
    return apiCall('/api/mind/vlm/analyze', {
      method: 'POST',
      body: JSON.stringify({
        artwork_image: artworkImage,
        image_format: imageFormat,
      }),
    });
  },

  // Get VLM service status
  getVLMStatus: async () => {
    return apiCall('/api/mind/vlm/status');
  },

  // Re-analyze artwork VLM emotion (for teachers)
  reanalyzeVLM: async (artworkId) => {
    return apiCall(`/api/mind/vlm/reanalyze/${artworkId}`, {
      method: 'POST',
    });
  },

  // Get psychological alerts
  getAlerts: async (alertLevel = null, resolved = null, days = 30) => {
    const params = new URLSearchParams();
    if (alertLevel) params.append('alert_level', alertLevel);
    if (resolved !== null) params.append('resolved', resolved);
    params.append('days', days);
    return apiCall(`/api/mind/alerts?${params.toString()}`);
  },

  // Resolve an alert
  resolveAlert: async (alertId, resolutionNote = '') => {
    return apiCall(`/api/mind/alerts/${alertId}/resolve?resolution_note=${encodeURIComponent(resolutionNote)}`, {
      method: 'PUT',
    });
  },

  // Get user MIND history
  getUserHistory: async (userId, days = 90) => {
    return apiCall(`/api/mind/analysis/${userId}/history?days=${days}`);
  },

  // Get dashboard summary
  getDashboardSummary: async (days = 30) => {
    return apiCall(`/api/mind/dashboard/summary?days=${days}`);
  },

  // ========== Hybrid MESA-VLM Scoring ==========

  // Get hybrid MESA scores (weights depend on assessment mode)
  getHybridScores: async (mesaScores, artworkImage = null, includeStructureVlm = true, mode = 'art_professional') => {
    return apiCall('/api/mind/hybrid-mesa', {
      method: 'POST',
      body: JSON.stringify({
        mesa_scores: mesaScores,
        artwork_image: artworkImage,
        include_structure_vlm: includeStructureVlm,
        mode: mode,
      }),
    });
  },

  // Get hybrid weight configuration
  getHybridWeights: async () => {
    return apiCall('/api/mind/hybrid-mesa/weights');
  },
};

// School API (for school admin portal)
export const schoolAPI = {
  // Authentication
  login: async (credentials) => {
    const response = await apiCall('/api/school/login', {
      method: 'POST',
      body: JSON.stringify(credentials),
    });
    // Clear ALL previous session data before setting new
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    localStorage.removeItem('school');
    // Store new session
    localStorage.setItem('token', response.access_token);
    localStorage.setItem('school', JSON.stringify(response.school));
    return response;
  },

  logout: () => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    localStorage.removeItem('school');
    // Clear any cached data that might interfere with new session
    Object.keys(localStorage).forEach(key => {
      if (key.startsWith('api_cache_') || key.startsWith('mesamind_')) {
        localStorage.removeItem(key);
      }
    });
  },

  getStoredSchool: () => {
    const school = localStorage.getItem('school');
    return school ? JSON.parse(school) : null;
  },

  getMe: async () => {
    return apiCall('/api/school/me');
  },

  // Dashboard
  getDashboard: async () => {
    return apiCall('/api/school/dashboard');
  },

  // Classes
  getClasses: async () => {
    return apiCall('/api/school/classes');
  },

  getClass: async (id) => {
    return apiCall(`/api/school/classes/${id}`);
  },

  createClass: async (data) => {
    return apiCall('/api/school/classes', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  },

  updateClass: async (id, data) => {
    return apiCall(`/api/school/classes/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  },

  deleteClass: async (id) => {
    return apiCall(`/api/school/classes/${id}`, {
      method: 'DELETE',
    });
  },

  permanentDeleteClass: async (id) => {
    return apiCall(`/api/school/classes/${id}/permanent`, {
      method: 'DELETE',
    });
  },

  bulkUploadClasses: async (classesData, decisions = {}) => {
    return apiCall('/api/school/classes/bulk-upload', {
      method: 'POST',
      body: JSON.stringify({ classes: classesData, decisions }),
    });
  },

  // Teachers
  getTeachers: async () => {
    return apiCall('/api/school/teachers');
  },

  getTeacher: async (teacherId) => {
    return apiCall(`/api/school/teachers/${teacherId}`);
  },

  createTeacher: async (teacherData) => {
    return apiCall('/api/school/teachers/create', {
      method: 'POST',
      body: JSON.stringify(teacherData),
    });
  },

  updateTeacher: async (teacherId, data) => {
    return apiCall(`/api/school/teachers/${teacherId}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  },

  resetTeacherPassword: async (teacherId, newPassword) => {
    return apiCall(`/api/school/teachers/${teacherId}/reset-password`, {
      method: 'POST',
      body: JSON.stringify({ new_password: newPassword }),
    });
  },

  addTeacher: async (teacherId) => {
    return apiCall('/api/school/teachers', {
      method: 'POST',
      body: JSON.stringify({ teacher_id: teacherId }),
    });
  },

  removeTeacher: async (teacherId) => {
    return apiCall(`/api/school/teachers/${teacherId}`, {
      method: 'DELETE',
    });
  },

  // Enhanced Teacher Management
  getAllTeachers: async (includeInactive = false) => {
    return apiCall(`/api/school/teachers/all?include_inactive=${includeInactive}`);
  },

  bulkUploadTeachers: async (teachersData, decisions = {}) => {
    return apiCall('/api/school/teachers/bulk-upload', {
      method: 'POST',
      body: JSON.stringify({ teachers: teachersData, decisions }),
    });
  },

  toggleTeacherStatus: async (teacherId) => {
    return apiCall(`/api/school/teachers/${teacherId}/toggle-status`, {
      method: 'POST',
    });
  },

  // Class Teachers
  getClassTeachers: async (classId) => {
    return apiCall(`/api/school/classes/${classId}/teachers`);
  },

  assignTeacherToClass: async (classId, teacherId, role = 'primary') => {
    return apiCall(`/api/school/classes/${classId}/teachers`, {
      method: 'POST',
      body: JSON.stringify({ teacher_id: teacherId, role }),
    });
  },

  removeTeacherFromClass: async (classId, teacherId) => {
    return apiCall(`/api/school/classes/${classId}/teachers/${teacherId}`, {
      method: 'DELETE',
    });
  },

  // Class Students
  getClassStudents: async (classId) => {
    return apiCall(`/api/school/classes/${classId}/students`);
  },

  removeStudentFromClass: async (classId, studentId) => {
    return apiCall(`/api/school/classes/${classId}/students/${studentId}`, {
      method: 'DELETE',
    });
  },

  // Dashboard Charts
  getDashboardCharts: async () => {
    return apiCall('/api/school/dashboard/charts');
  },

  // School Students (all students across all classes)
  getStudents: async () => {
    return apiCall('/api/school/students');
  },

  // Enhanced Student Management
  getAllStudents: async (includeInactive = false) => {
    return apiCall(`/api/school/students/all?include_inactive=${includeInactive}`);
  },

  getStudent: async (studentId) => {
    return apiCall(`/api/school/students/${studentId}`);
  },

  createStudent: async (studentData) => {
    return apiCall('/api/school/students/create', {
      method: 'POST',
      body: JSON.stringify(studentData),
    });
  },

  bulkUploadStudents: async (studentsData, decisions = {}) => {
    return apiCall('/api/school/students/bulk-upload', {
      method: 'POST',
      body: JSON.stringify({ students: studentsData, decisions }),
    });
  },

  enrollExistingStudentsBulk: async (classId, studentsData) => {
    return apiCall(`/api/school/classes/${classId}/enroll-students-bulk`, {
      method: 'POST',
      body: JSON.stringify({ students: studentsData }),
    });
  },

  updateStudent: async (studentId, data) => {
    return apiCall(`/api/school/students/${studentId}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  },

  resetStudentPassword: async (studentId, newPassword) => {
    return apiCall(`/api/school/students/${studentId}/reset-password`, {
      method: 'POST',
      body: JSON.stringify({ new_password: newPassword }),
    });
  },

  toggleStudentStatus: async (studentId) => {
    return apiCall(`/api/school/students/${studentId}/toggle-status`, {
      method: 'POST',
    });
  },

  deleteStudent: async (studentId) => {
    return apiCall(`/api/school/students/${studentId}`, {
      method: 'DELETE',
    });
  },

  enrollStudent: async (studentId, classId) => {
    return apiCall(`/api/school/students/${studentId}/enroll`, {
      method: 'POST',
      body: JSON.stringify({ class_id: classId }),
    });
  },

  unenrollStudent: async (studentId, classId) => {
    return apiCall(`/api/school/students/${studentId}/unenroll/${classId}`, {
      method: 'DELETE',
    });
  },

  // School Alerts
  getAlerts: async (filter = {}) => {
    const params = new URLSearchParams();
    if (filter.resolved !== undefined) params.append('resolved', filter.resolved);
    if (filter.alert_level) params.append('alert_level', filter.alert_level);
    if (filter.days) params.append('days', filter.days);
    const queryString = params.toString();
    return apiCall(`/api/school/alerts${queryString ? `?${queryString}` : ''}`);
  },

  resolveAlert: async (alertId, resolutionNote = '') => {
    return apiCall(`/api/school/alerts/${alertId}/resolve?resolution_note=${encodeURIComponent(resolutionNote)}`, {
      method: 'PUT',
    });
  },

  // Principal Dashboard API
  getPrincipalDashboard: async () => {
    return apiCall('/api/principal/dashboard');
  },

  getMESADistribution: async () => {
    return apiCall('/api/principal/mesa-distribution');
  },

  getStudentRankings: async (limit = 20) => {
    return apiCall(`/api/principal/rankings?limit=${limit}`);
  },

  getAttentionStudents: async (limit = 20) => {
    return apiCall(`/api/principal/attention-students?limit=${limit}`);
  },

  getSubjectStatistics: async () => {
    return apiCall('/api/principal/subject-statistics');
  },

  getGradeComparisons: async () => {
    return apiCall('/api/principal/grade-comparisons');
  },

  getMESATrend: async (period = 'week') => {
    return apiCall(`/api/principal/mesa-trend?period=${period}`);
  },
};

// Admin School Management API (for platform admin)
export const adminSchoolAPI = {
  // Schools
  getSchools: async () => {
    return apiCall('/api/admin/schools');
  },

  getSchool: async (id) => {
    return apiCall(`/api/admin/schools/${id}`);
  },

  createSchool: async (data) => {
    return apiCall('/api/admin/schools', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  },

  updateSchool: async (id, data) => {
    return apiCall(`/api/admin/schools/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  },

  deleteSchool: async (id) => {
    return apiCall(`/api/admin/schools/${id}`, {
      method: 'DELETE',
    });
  },

  resetSchoolPassword: async (id, newPassword) => {
    return apiCall(`/api/admin/schools/${id}/reset-password`, {
      method: 'POST',
      body: JSON.stringify({ new_password: newPassword }),
    });
  },

  changeSchoolUsername: async (id, newUsername) => {
    return apiCall(`/api/admin/schools/${id}/change-username`, {
      method: 'POST',
      body: JSON.stringify({ new_username: newUsername }),
    });
  },

  toggleSchoolStatus: async (id) => {
    return apiCall(`/api/admin/schools/${id}/toggle-status`, {
      method: 'POST',
    });
  },

  getSchoolStats: async (id) => {
    return apiCall(`/api/admin/schools/${id}/stats`);
  },

  // Available teachers
  getAvailableTeachers: async () => {
    return apiCall('/api/admin/teachers/available');
  },

  // Psychological alerts by school
  getAlertsBySchool: async () => {
    return apiCall('/api/admin/alerts-by-school');
  },
};

// Admin Evaluation Mode API
export const adminEvaluationModeAPI = {
  getModes: async () => {
    return apiCall('/api/admin/evaluation-modes');
  },

  getMode: async (modeId) => {
    return apiCall(`/api/admin/evaluation-modes/${modeId}`);
  },

  createMode: async (data) => {
    return apiCall('/api/admin/evaluation-modes', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  },

  updateMode: async (modeId, data) => {
    return apiCall(`/api/admin/evaluation-modes/${modeId}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  },

  deleteMode: async (modeId) => {
    return apiCall(`/api/admin/evaluation-modes/${modeId}`, {
      method: 'DELETE',
    });
  },
};

// Reports API
export const reportsAPI = {
  // Student Report
  getStudentReport: async (studentId, days = 30) => {
    return apiCall(`/api/reports/student/${studentId}?days=${days}`);
  },

  downloadStudentReport: async (studentId, format = 'json') => {
    return apiCall(`/api/reports/student/${studentId}/download?format=${format}`);
  },

  // Class Report
  getClassReport: async (classId) => {
    return apiCall(`/api/reports/class/${classId}`);
  },

  getClassSummary: async (classId) => {
    return apiCall(`/api/reports/class/${classId}/summary`);
  },

  getClassObjectiveData: async (classId) => {
    return apiCall(`/api/reports/class/${classId}/objective-data`);
  },

  // School Report
  getSchoolReport: async (schoolId) => {
    return apiCall(`/api/reports/school/${schoolId}`);
  },

  getSchoolSummary: async (schoolId) => {
    return apiCall(`/api/reports/school/${schoolId}/summary`);
  },
};

export default apiCall;
